import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import ImageZoomModal from "./ImageZoomModal";

const Container = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
        padding: 1rem;
        margin: 0 1rem;
    }
`;

const SearchImageContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
`;

const SearchImage = styled.img`
    width: 400px;
    height: 400px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #ddd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    
    @media (max-width: 768px) {
        width: 100%;
        height: 250px;
        max-width: 300px;
    }
    
    @media (max-width: 480px) {
        width: 100%;
        height: 200px;
        max-width: 250px;
    }

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }
`;

const FloatingImageContainer = styled.div`
    position: fixed;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    z-index: 1000;
    opacity: ${props => props.isVisible ? 1 : 0};
    visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
    pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
    
    @media (max-width: 768px) {
        right: 10px;
        top: 40%;
    }
    
    @media (max-width: 480px) {
        right: 5px;
        top: 35%;
    }
`;

const FloatingImage = styled.img`
    width: 150px;
    height: 150px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #ddd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    background: white;
    
    @media (max-width: 768px) {
        width: 120px;
        height: 120px;
    }
    
    @media (max-width: 480px) {
        width: 100px;
        height: 100px;
    }

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
    }
`;

const FloatingImageLabel = styled.div`
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    white-space: nowrap;
    pointer-events: none;
    
    @media (max-width: 480px) {
        font-size: 0.7rem;
        padding: 3px 6px;
    }
`;

const MetadataContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 3rem;
    margin-bottom: 2rem;
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 6px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }
    
    @media (max-width: 480px) {
        gap: 0.5rem;
    }
`;

const MetadataItem = styled.div`
    text-align: center;
`;

const MetadataLabel = styled.div`
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
`;

const MetadataValue = styled.div`
    color: #6c757d;
    font-size: 0.9rem;
`;

const SiteUrlLink = styled.a`
    color: #007bff;
    text-decoration: none;
    font-size: 0.9rem;
    
    &:hover {
        text-decoration: underline;
        color: #0056b3;
    }
    
    &:visited {
        color: #6f42c1;
    }
`;

const ResultsHeader = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
`;

const ResultsTitle = styled.h3`
    margin: 0;
    color: #2c3e50;
    font-size: 1.3rem;
`;

const ResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 1fr;
        gap: 0.75rem;
    }
    
    @media (max-width: 480px) {
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem;
    }
`;

const ResultCard = styled.div`
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    display: flex;
    gap: 1rem;
    transition: box-shadow 0.2s;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.5rem;
        padding: 0.75rem;
    }
    
    @media (max-width: 480px) {
        padding: 0.5rem;
        gap: 0.25rem;
    }

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

const ResultImage = styled.img`
    width: 200px;
    height: 150px;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid #ddd;
    flex-shrink: 0;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    
    @media (max-width: 768px) {
        width: 100%;
        height: 120px;
        margin: 0 auto;
        display: block;
    }
    
    @media (max-width: 480px) {
        height: 100px;
    }

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
`;

const ResultInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const ResultName = styled.a`
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    color: #2c3e50 !important;
    text-decoration: none !important;
    display: block;
    cursor: pointer;
    word-wrap: break-word;
    line-height: 1.4;
    
    @media (max-width: 768px) {
        font-size: 0.85rem;
        margin-bottom: 0.25rem;
    }
    
    @media (max-width: 480px) {
        font-size: 0.8rem;
        line-height: 1.3;
    }

    &:hover {
        color: #007bff !important;
        text-decoration: underline !important;
    }

    &:visited {
        color: #2c3e50 !important;
    }

    &:visited:hover {
        color: #007bff !important;
        text-decoration: underline !important;
    }

    &:focus {
        color: #007bff !important;
        text-decoration: underline !important;
    }
`;

const ResultId = styled.div`
    font-size: 0.8rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
    }
    
    @media (max-width: 480px) {
        font-size: 0.7rem;
    }
`;

const AnnotationControls = styled.div`
    margin-top: 0.75rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    
    @media (max-width: 768px) {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
        margin-top: 0.5rem;
    }
    
    @media (max-width: 480px) {
        gap: 0.2rem;
        margin-top: 0.25rem;
    }
`;

const RatingButton = styled.button`
    padding: 0.3rem 0.6rem;
    border: 1.5px solid;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.2s;
    
    @media (max-width: 768px) {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
    }
    
    @media (max-width: 480px) {
        font-size: 0.7rem;
        padding: 0.2rem 0.4rem;
    }

    ${(props) =>
        props.variant === "good" &&
        `
    border-color: #28a745;
    background-color: ${props.active ? "#28a745" : "white"};
    color: ${props.active ? "white" : "#28a745"};
  `}

    ${(props) =>
        props.variant === "bad" &&
        `
    border-color: #dc3545;
    background-color: ${props.active ? "#dc3545" : "white"};
    color: ${props.active ? "white" : "#dc3545"};
  `}
  
  &:hover {
        ${(props) =>
            props.variant === "good" &&
            `
      background-color: #28a745;
      color: white;
    `}

        ${(props) =>
            props.variant === "bad" &&
            `
      background-color: #dc3545;
      color: white;
    `}
    }
`;

const RadioContainer = styled.div`
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
`;

const RadioOption = styled.label`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: #495057;
    cursor: pointer;
`;

const RadioInput = styled.input`
    margin: 0;
`;

const SaveButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 0;
    border-top: 2px solid #dee2e6;
    margin-top: 1rem;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem 0;
    }
`;

const SaveButton = styled.button`
    background-color: #27ae60;
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1.1rem;
    font-weight: 600;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:hover {
        background-color: #229954;
    }

    &:disabled {
        background-color: #bdc3c7;
        cursor: not-allowed;
    }
`;

// Commented out unused styled components
/* const ProgressInfo = styled.div`
    color: #6c757d;
    font-size: 0.9rem;
`;

const NavigationButtons = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const NavButton = styled.button`
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
        background-color: #5a6268;
    }

    &:disabled {
        background-color: #bdc3c7;
        cursor: not-allowed;
    }
`; */

function SingleProductEvaluator({ 
    searches, 
    annotations, 
    onAnnotationChange, 
    currentSearchIndex, 
    onSaveAndNext,
    onNavigate
}) {
    const [hiddenResults, setHiddenResults] = useState(new Set());
    const [zoomModal, setZoomModal] = useState({
        isOpen: false,
        imageUrl: '',
        imageAlt: '',
        imageInfo: ''
    });
    const [isMainImageVisible, setIsMainImageVisible] = useState(true);
    const mainImageRef = useRef(null);

    // Reset hidden results when current search changes or searches data changes
    useEffect(() => {
        setHiddenResults(new Set());
    }, [currentSearchIndex, searches]);

    // Scroll to top when current search changes
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [currentSearchIndex]);

    // Intersection Observer to track main image visibility
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsMainImageVisible(entry.isIntersecting);
            },
            {
                threshold: 0.1, // Trigger when 10% of the image is visible
                rootMargin: '-50px 0px' // Add some margin to trigger earlier
            }
        );

        const currentElement = mainImageRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, [currentSearchIndex]); // Re-run when search changes

    if (!searches || searches.length === 0) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                    No search data available
                </div>
            </Container>
        );
    }

    if (currentSearchIndex >= searches.length) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <h2 style={{ color: '#27ae60', marginBottom: '1rem' }}>
                        🎉 All searches completed!
                    </h2>
                    <p style={{ color: '#6c757d' }}>
                        You have annotated all search products in this dataset.
                    </p>
                </div>
            </Container>
        );
    }

    const currentSearch = searches[currentSearchIndex];

    const handleRatingChange = (resultIndex, rating) => {
        const currentAnnotation = annotations[`search_${currentSearchIndex}`]?.[`result_${resultIndex}`] || {};
        const newRating = currentAnnotation.rating === rating ? null : rating;

        onAnnotationChange(currentSearchIndex, resultIndex, {
            ...currentAnnotation,
            rating: newRating,
        });

        // Hide the result when marked as "bad"
        const resultKey = `${currentSearchIndex}_${resultIndex}`;
        if (newRating === "bad") {
            setHiddenResults(prev => new Set(prev).add(resultKey));
        } else if (newRating === null) {
            // Show the result again if rating is removed
            setHiddenResults(prev => {
                const newSet = new Set(prev);
                newSet.delete(resultKey);
                return newSet;
            });
        }
    };

    const handleNoteChange = (resultIndex, note) => {
        onAnnotationChange(currentSearchIndex, resultIndex, {
            ...annotations[`search_${currentSearchIndex}`]?.[`result_${resultIndex}`],
            note
        });
    };

    const openZoomModal = (imageUrl, imageAlt, imageInfo = '') => {
        setZoomModal({
            isOpen: true,
            imageUrl,
            imageAlt,
            imageInfo
        });
    };

    const closeZoomModal = () => {
        setZoomModal({
            isOpen: false,
            imageUrl: '',
            imageAlt: '',
            imageInfo: ''
        });
    };

    // Filter out hidden results
    const visibleResults = currentSearch.results.filter((_, resultIndex) => 
        !hiddenResults.has(`${currentSearchIndex}_${resultIndex}`)
    );

    const getAnnotatedResultsCount = () => {
        return currentSearch.results.filter((_, resultIndex) =>
            annotations[`search_${currentSearchIndex}`]?.[`result_${resultIndex}`]?.rating
        ).length;
    };

    // Commented out unused variable
    // const isSearchComplete = getAnnotatedResultsCount() === currentSearch.results.length;

    return (
        <Container>
            <MetadataContainer>
                <MetadataItem>
                    <MetadataLabel>Site URL</MetadataLabel>
                    <MetadataValue>
                        <SiteUrlLink
                            href={currentSearch.site_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {currentSearch.site_url}
                        </SiteUrlLink>
                    </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                    <MetadataLabel>Site UUID</MetadataLabel>
                    <MetadataValue>{currentSearch.site_uuid}</MetadataValue>
                </MetadataItem>
                <MetadataItem>
                    <MetadataLabel>Store ID</MetadataLabel>
                    <MetadataValue>{currentSearch.store_id}</MetadataValue>
                </MetadataItem>
                <MetadataItem>
                    <MetadataLabel>Results</MetadataLabel>
                    <MetadataValue>
                        {visibleResults.length}
                        {visibleResults.length !== currentSearch.results.length && (
                            <span style={{ fontSize: '0.8rem', color: '#6c757d', marginLeft: '0.25rem' }}>
                                ({currentSearch.results.length - visibleResults.length} hidden)
                            </span>
                        )}
                    </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                    <MetadataLabel>Annotated</MetadataLabel>
                    <MetadataValue>
                        {getAnnotatedResultsCount()}/{currentSearch.results.length}
                    </MetadataValue>
                </MetadataItem>
            </MetadataContainer>

            <SearchImageContainer ref={mainImageRef}>
                <SearchImage
                    src={currentSearch.searched_image_url}
                    alt="Search query"
                    title="Click to zoom"
                    onClick={() => openZoomModal(
                        currentSearch.searched_image_url,
                        "Search query image"
                    )}
                    onError={(e) => {
                        e.target.style.display = "none";
                    }}
                />
            </SearchImageContainer>

            <ResultsHeader>
                <ResultsTitle>Search Results</ResultsTitle>
            </ResultsHeader>

            <ResultsGrid>
                {visibleResults.map((result) => {
                    // Find the original index from the full results array
                    const resultIndex = currentSearch.results.findIndex(r => r === result);
                    const annotation = annotations[`search_${currentSearchIndex}`]?.[`result_${resultIndex}`] || {};

                    return (
                        <ResultCard key={resultIndex}>
                            <ResultImage
                                src={result.image_url}
                                alt={result.product_name}
                                title="Click to zoom"
                                onClick={() => openZoomModal(
                                    result.image_url,
                                    result.product_name
                                )}
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                            <ResultInfo>
                                <ResultName
                                    href={result.product_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={result.product_url}
                                >
                                    {result.product_name}
                                </ResultName>
                                <ResultId>ID: {result.product_id}</ResultId>

                                <AnnotationControls>
                                    <RatingButton
                                        variant="bad"
                                        active={annotation.rating === "bad"}
                                        onClick={() => handleRatingChange(resultIndex, "bad")}
                                    >
                                        ✗ Bad
                                    </RatingButton>
                                </AnnotationControls>

                                <RadioContainer>
                                    <RadioOption>
                                        <RadioInput
                                            type="radio"
                                            name={`note_${currentSearchIndex}_${resultIndex}`}
                                            value="Wrong Type"
                                            checked={annotation.note === "Wrong Type"}
                                            onChange={(e) => handleNoteChange(resultIndex, e.target.value)}
                                        />
                                        Wrong Type
                                    </RadioOption>
                                    <RadioOption>
                                        <RadioInput
                                            type="radio"
                                            name={`note_${currentSearchIndex}_${resultIndex}`}
                                            value="Wrong Color"
                                            checked={annotation.note === "Wrong Color"}
                                            onChange={(e) => handleNoteChange(resultIndex, e.target.value)}
                                        />
                                        Wrong Color
                                    </RadioOption>
                                </RadioContainer>
                            </ResultInfo>
                        </ResultCard>
                    );
                })}
            </ResultsGrid>

            <SaveButtonContainer>
                {/* Commented out navigation buttons as requested */}
                {/* <NavigationButtons>
                    <NavButton
                        onClick={() => onNavigate(currentSearchIndex - 1)}
                        disabled={currentSearchIndex === 0}
                    >
                        ← Previous
                    </NavButton>
                    <NavButton
                        onClick={() => onNavigate(currentSearchIndex + 1)}
                        disabled={currentSearchIndex >= searches.length - 1}
                    >
                        Next →
                    </NavButton>
                </NavigationButtons> */}
                
                {/* Commented out progress info as requested */}
                {/* <ProgressInfo>
                    Search {currentSearchIndex + 1} of {searches.length}
                    {isSearchComplete && " ✓"}
                </ProgressInfo> */}
                
                <SaveButton
                    onClick={onSaveAndNext}
                    disabled={false}
                >
                    Save & Next
                </SaveButton>
            </SaveButtonContainer>

            {/* Floating image that appears when main image is out of view */}
            <FloatingImageContainer isVisible={!isMainImageVisible}>
                <FloatingImage
                    src={currentSearch.searched_image_url}
                    alt="Search query (floating)"
                    title="Click to zoom - Search query image"
                    onClick={() => openZoomModal(
                        currentSearch.searched_image_url,
                        "Search query image"
                    )}
                    onError={(e) => {
                        e.target.style.display = "none";
                    }}
                />
                <FloatingImageLabel>Searched Image</FloatingImageLabel>
            </FloatingImageContainer>

            <ImageZoomModal
                isOpen={zoomModal.isOpen}
                imageUrl={zoomModal.imageUrl}
                imageAlt={zoomModal.imageAlt}
                imageInfo={zoomModal.imageInfo}
                onClose={closeZoomModal}
            />
        </Container>
    );
}

export default SingleProductEvaluator;
